import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { FaCertificate } from "react-icons/fa";
import SitePicture from "./SitePicture";
import { ISO } from "../lib/contact";

const Carousel = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleNext = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  }, [images.length]);

  useEffect(() => {
    const interval = setInterval(() => {
      handleNext();
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [handleNext]);

  const handlePrev = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const handleIndicatorClick = (index) => {
    setCurrentIndex(index);
  };

  return (
    <section
      className="relative w-full h-[72vh] min-h-[480px] max-h-[820px] md:h-[calc(100vh-84px)] overflow-hidden bg-brand-navy-dark"
      aria-roledescription="carousel"
      aria-label="Featured work"
    >
      {/* Carousel Wrapper */}
      <div
        className="flex transition-transform duration-700 ease-in-out h-full"
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        {images.map((image, index) => (
          <div
            key={index}
            className="relative flex-shrink-0 w-full h-full"
            aria-hidden={currentIndex !== index}
            // Off-screen slides: also remove their buttons from the tab order
            {...(currentIndex !== index ? { inert: "" } : {})}
          >
            <SitePicture
              name={`hero/${image.src.replace(/\.[^.]+$/, "")}`}
              alt={image.title}
              sizes="100vw"
              loading={index === 0 ? "eager" : "lazy"}
              fetchpriority={index === 0 ? "high" : undefined}
              className="w-full h-full object-cover"
            />
            {/* Brand-tinted overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-brand-navy-dark/90 via-brand-navy-dark/60 to-brand-navy-dark/20" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />

            {/* Slide content */}
            <div className="absolute inset-0 z-10 flex items-center">
              <div className="w-full max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">
                <div className="max-w-2xl text-white">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <span className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-brand-yellow">
                      <span className="h-0.5 w-8 bg-brand-yellow" />
                      R.C. Bidave Civil Contractors
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-yellow px-3 py-1 text-xs font-bold text-brand-navy">
                      <FaCertificate aria-hidden="true" /> {ISO.standard} Certified
                    </span>
                  </div>
                  {/* One <h1> per page: the first slide's title; the rest are <h2> */}
                  {React.createElement(
                    index === 0 ? "h1" : "h2",
                    { className: "mt-4 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight" },
                    image.title
                  )}
                  <p className="mt-4 text-sm sm:text-base md:text-lg text-gray-100 leading-relaxed">
                    {image.description}
                  </p>
                  <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
                    <Link
                      to="/projects-gallery"
                      className="inline-flex justify-center items-center bg-brand-yellow text-brand-navy font-bold px-7 py-3.5 rounded-full shadow-lg hover:bg-brand-gold hover:-translate-y-0.5 transition-all duration-300"
                    >
                      View Our Projects
                    </Link>
                    <Link
                      to="/contact-us"
                      className="inline-flex justify-center items-center border-2 border-white/80 text-white font-semibold px-7 py-3 rounded-full hover:bg-white hover:text-brand-navy transition-all duration-300"
                    >
                      Get in Touch
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={handlePrev}
        aria-label="Previous slide"
        className="hidden sm:flex absolute top-1/2 left-4 transform -translate-y-1/2 bg-white/90 backdrop-blur-sm text-[#2f2483] w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full shadow-lg hover:shadow-2xl hover:bg-[#ffed00] hover:scale-110 transition-all duration-300 z-20 group"
      >
        <svg className="w-5 h-5 sm:w-6 sm:h-6 group-hover:scale-125 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <button
        onClick={handleNext}
        aria-label="Next slide"
        className="hidden sm:flex absolute top-1/2 right-4 transform -translate-y-1/2 bg-white/90 backdrop-blur-sm text-[#2f2483] w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full shadow-lg hover:shadow-2xl hover:bg-[#ffed00] hover:scale-110 transition-all duration-300 z-20 group"
      >
        <svg className="w-5 h-5 sm:w-6 sm:h-6 group-hover:scale-125 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Indicators */}
      <div className="absolute bottom-4 sm:bottom-6 md:bottom-8 left-1/2 transform -translate-x-1/2 flex justify-center gap-0.5 z-20 bg-black/20 backdrop-blur-sm px-3 py-1 rounded-full">
        {images.map((_, index) => (
          <button
            key={index}
            onClick={() => handleIndicatorClick(index)}
            aria-label={`Go to slide ${index + 1}`}
            aria-current={currentIndex === index ? "true" : undefined}
            className="group flex h-6 min-w-[24px] items-center justify-center px-1"
          >
            <span
              className={`block rounded-full transition-all duration-300 ${
                currentIndex === index
                  ? "bg-[#ffed00] w-8 h-3"
                  : "bg-white/60 group-hover:bg-white w-3 h-3 group-hover:scale-125"
              }`}
            />
          </button>
        ))}
      </div>
    </section>
  );
};

export default Carousel;
